/**
 * The sign-in frame, laid out exactly like SynRank's (the Syntera reference):
 * a split card on a soft radial ground — product story on the left (heading,
 * three metric tiles, three features, trust ticks), the form on the right
 * with the "secure login" footer. Colours are SynAnalytics light teal.
 */

import type { ReactNode } from 'react';

import { PortalLoginFrame, usePortalBrand } from '../portal/PortalBrand.tsx';

// Illustrative tiles that show what the product does, as on SynRank's sign-in.
const METRICS = [
  { label: 'Clients', value: '24', badge: 'all reporting', good: true },
  { label: 'Reports', value: '48', badge: 'sent this month', good: null },
  { label: 'Portal', value: '19', badge: 'clients signed in', good: true },
];

const FEATURES = [
  {
    icon: '▦',
    title: 'Live client dashboards',
    desc: 'Drag-and-drop dashboards for every client, live from Google Ads, accounting, sales, leads and reviews.',
  },
  {
    icon: '▤',
    title: 'Reports that present themselves',
    desc: 'Slide decks and documents with a cover, contents and your notes, ready to present every month.',
  },
  {
    icon: '◎',
    title: 'Your brand, your client portal',
    desc: 'Clients sign in under your agency’s logo and see only what you share with them.',
  },
];

/** The Syntera-family logo: emblem plus the navy metallic wordmark (tools/make-brand.py). */
export function Wordmark({ large = false }: { large?: boolean }): ReactNode {
  return (
    <img
      className={`li-brand${large ? ' li-brand-lg' : ''}`}
      src="/synanalytics-logo.png?v=1"
      srcSet="/synanalytics-logo.png?v=1 1x, /synanalytics-logo@2x.png?v=1 2x"
      alt="SynAnalytics"
    />
  );
}

export function LoginShell({ children }: { children: ReactNode }): ReactNode {
  // C7 — on an agency's portal host the frame is the agency's, not ours.
  const brand = usePortalBrand();
  if (brand) return <PortalLoginFrame brand={brand}>{children}</PortalLoginFrame>;
  return (
    <main className="li">
      <div className="li-card">
        <aside className="li-left" aria-label="About SynAnalytics">
          <div>
            <a href="https://analytics.synteralabs.co.za" className="li-logo">
              <Wordmark large />
            </a>
            <p className="li-pitch">
              Client reporting,
              <br />
              done in minutes.
            </p>
            <p className="li-sub">
              Dashboards, monthly reports and a branded client portal for South African agencies,
              built from every client's live data.
            </p>
            <div className="li-metrics">
              {METRICS.map((metric) => (
                <div className="li-metric" key={metric.label}>
                  <span className="li-metric-label">{metric.label}</span>
                  <strong className="li-metric-value">{metric.value}</strong>
                  <span className={`li-badge${metric.good ? ' li-badge-good' : ''}`}>
                    {metric.badge}
                  </span>
                </div>
              ))}
            </div>
            <ul className="li-features">
              {FEATURES.map((feature) => (
                <li key={feature.title}>
                  <span className="li-feature-icon" aria-hidden="true">
                    {feature.icon}
                  </span>
                  <span>
                    <strong>{feature.title}</strong>
                    <small>{feature.desc}</small>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="li-trust">
            {['POPIA Compliant', 'Cloudflare Protected'].map((item) => (
              <span key={item}>
                <span className="li-tick" aria-hidden="true">
                  ✓
                </span>
                {item}
              </span>
            ))}
          </div>
        </aside>

        <section className="li-right">
          <div className="li-mobile-logo">
            <Wordmark large />
          </div>
          {children}
          <div className="li-divider" aria-hidden="true">
            <span />
            secure login
            <span />
          </div>
          <div className="li-secure">
            <span>🔒 256-bit encrypted</span>
            <span>🛡️ POPIA compliant</span>
            <span>🇿🇦 South African</span>
          </div>
        </section>
      </div>
    </main>
  );
}

/** SynRank's live password checklist, against the same rule the server enforces. */
export const PASSWORD_RULES: { label: string; test: (value: string) => boolean }[] = [
  { label: 'At least 12 characters', test: (value) => value.length >= 12 },
  { label: 'A capital letter', test: (value) => /[A-Z]/.test(value) },
  { label: 'A number', test: (value) => /\d/.test(value) },
  { label: 'A symbol', test: (value) => /[^A-Za-z0-9]/.test(value) },
];

export function PasswordChecklist({
  value,
  confirm,
}: {
  value: string;
  confirm: string;
}): ReactNode {
  return (
    <ul className="li-checklist" aria-live="polite">
      {PASSWORD_RULES.map((rule) => {
        const ok = rule.test(value);
        return (
          <li key={rule.label} className={ok ? 'ok' : ''}>
            <span aria-hidden="true">{ok ? '✓' : '○'}</span> {rule.label}
          </li>
        );
      })}
      <li className={confirm.length > 0 && confirm === value ? 'ok' : ''}>
        <span aria-hidden="true">{confirm.length > 0 && confirm === value ? '✓' : '○'}</span> Both
        passwords match
      </li>
    </ul>
  );
}
